import { ParsedUrlQueryInput } from 'querystring';
import { FormEvent, useEffect, useState } from 'react';
import {
  Center,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Heading,
  ModalHeader,
  HStack,
  VStack,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { Button } from 'components';

import { Paths } from 'utils/paths';
import { resolvePath } from 'utils/helpers';
import { usePageTextContent } from 'context/pageTextContent';
import { useSearchPageStore } from '../../useSearchPageStore';
import { IncludedNetworkSection } from './IncludedNetworkSection';
import { NearInput } from './NearInput';
import { CategoriesSection } from './CategoriesSection';

interface Props {
  categories: Category[];
}

export const FiltersModal = (props: Props) => {
  const { categories } = props;

  const [searchNear, setSearchNear] = useState('');
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [areNetworksIncluded, setAreNetworksIncluded] = useState(true);

  const { content } = usePageTextContent<SearchPageContent>();

  const { filtersTitle, clearFiltersButton, applyFiltersButton } = content;

  const isFiltersModalOpen = useSearchPageStore(
    state => state.isFiltersModalOpen,
  );
  const onCloseFiltersModal = useSearchPageStore(
    state => state.onCloseFiltersModal,
  );

  const includeNetworks = useSearchPageStore(state => state.includeNetworks);
  const onIncludeNetworksChange = useSearchPageStore(
    state => state.onIncludeNetworksChange,
  );

  const near = useSearchPageStore(state => state.near);
  const onNearChange = useSearchPageStore(state => state.onNearChange);

  const selectedCategories = useSearchPageStore(
    state => state.selectedCategories,
  );
  const onSelectedCategoriesChange = useSearchPageStore(
    state => state.onSelectedCategoriesChange,
  );

  const setIsLoading = useSearchPageStore(state => state.setIsLoading);

  useEffect(() => {
    if (!isFiltersModalOpen) return;

    setSearchNear(near);
    setFilterCategories(selectedCategories);
    setAreNetworksIncluded(includeNetworks);
  }, [isFiltersModalOpen]);

  const router = useRouter();

  const onSearchNearChange = (value: string) => {
    setSearchNear(value);
  };

  const onCategoryChange = (categoryId: string) => {
    if (filterCategories.includes(categoryId)) {
      const _filterCategories = filterCategories.filter(
        category => category !== categoryId,
      );

      setFilterCategories(_filterCategories);

      return;
    }

    const _filterCategories = [...filterCategories, categoryId];

    setFilterCategories(_filterCategories);
  };

  const isCategoryChecked = (categoryId: string) => {
    return filterCategories.includes(categoryId);
  };

  const onSubmit = (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault();

    const categoriesParam = filterCategories.join(',');

    const query: ParsedUrlQueryInput = { page: 1 };

    if (searchNear) query.near = searchNear;

    if (categoriesParam) query.categoryId = categoriesParam;

    if (areNetworksIncluded !== includeNetworks) {
      query.includeNetworks = areNetworksIncluded;
    }

    onSelectedCategoriesChange(filterCategories);
    onNearChange(searchNear);
    onIncludeNetworksChange(areNetworksIncluded);

    setIsLoading(true);

    const pageUrl = resolvePath({ pathname: Paths.SEARCH, query });

    router.replace(pageUrl);

    onCloseFiltersModal();
  };

  const onAreNetworksIncludedChange = (value: boolean) => {
    setAreNetworksIncluded(value);
  };

  const onClearFilters = () => {
    setSearchNear('');
    setFilterCategories([]);
  };

  return (
    <Modal
      isOpen={isFiltersModalOpen}
      onClose={onCloseFiltersModal}
      isCentered
      size={['full', 'full', '3xl']}
      autoFocus={false}
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="rgba(14, 38, 69, 0.77)" />

      <ModalContent as="form" onSubmit={onSubmit} w="full" rounded="xl">
        <ModalHeader as={HStack} py="1rem" borderTopRadius={['none', 'md']}>
          <Center w="full">
            <Heading fontSize="1.25rem" fontWeight="normal" lineHeight="1.5">
              {filtersTitle}
            </Heading>
          </Center>

          <ModalCloseButton
            bg="white"
            rounded="full"
            top="1rem"
            as={motion.button}
            borderColor="gray.100"
            borderWidth="1px"
            borderStyle="solid"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            shadow="lg"
          />
        </ModalHeader>

        <ModalBody
          as={VStack}
          spacing="1.5rem"
          w="full"
          align="flex-start"
          py={0}
          px={0}
          bg="gray.50"
        >
          <IncludedNetworkSection
            isIncluded={areNetworksIncluded}
            onChange={onAreNetworksIncludedChange}
          />

          <NearInput onChange={onSearchNearChange} value={searchNear} />

          <CategoriesSection
            onChange={onCategoryChange}
            categories={categories}
            isChecked={isCategoryChecked}
          />
        </ModalBody>

        <ModalFooter
          as={HStack}
          w="full"
          py="1rem"
          borderBottomRadius={['none', 'md']}
        >
          <HStack w="full" justify="space-between">
            <Button
              variant="link"
              textDecoration="underline"
              onClick={onClearFilters}
            >
              {clearFiltersButton}
            </Button>

            <Button variant="zmyle" type="submit">
              {applyFiltersButton}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
